import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getQuizResults, saveAiFeedback } from '../lib/supabase';
import { supabase } from '../config/supabase';
import { getCourse, getCourseList } from '../config/courses';
import { callClaude } from '../lib/claude';
import { QuizQuestion, Course } from '../types';
import { LoadingSpinner } from './LoadingSpinner';

interface LernstandFeedback {
  BEGRUESSUNG?: string;
  STARK?: string;
  UEBEN?: string;
  TIPP?: string;
  EMPFEHLUNG?: string;
  MOTIVATION?: string;
}

// All progress data for the AI
interface FullProgressData {
  // Quiz
  quizCount: number;
  quizAvg: number;
  quizLast: number | null;
  quizWrongByCategory: Record<string, number>;

  // Fill-in
  fillInCount: number;
  fillInAvg: number;
  fillInLast: number | null;

  // Transform
  transformCount: number;
  transformAvg: number;
  transformLast: number | null;

  // Flashcards by category
  flashcardsByCategory: Record<string, { known: number; unknown: number; total: number }>;
  flashcardsTotalKnown: number;
  flashcardsTotalUnknown: number;

  // Vocab Match
  vocabMatchCount: number;
  vocabMatchAvgEfficiency: number;

  // Lessons
  lessonsRead: string[];
  lessonsNotRead: string[];

  // Last activity
  lastActivityType: string;
  lastActivityTime: string;
}

// Category labels for display
const categoryLabels: Record<string, string> = {
  // Grammar categories
  'some-any': 'some/any + compounds',
  'conditional-iii': 'Conditional III',
  'gerund-infinitive': 'Gerund/Infinitive',
  'want-tell-ask': 'want/tell/ask',
  'indirect-speech': 'Indirect Speech',
  'word-order': 'Word Order',
  // Vocab categories
  'vocab-unit6': 'Vokabeln Unit 6',
  'vocab-australia': 'Vokabeln Australia',
  'vocab-unit7': 'Vokabeln Unit 7',
  'content': 'Content/Inhalt',
  // EU Geo categories
  grundsaetze: 'Grundsaetze',
  vorteile: 'Vorteile',
  nachteile: 'Nachteile',
  aufgaben: 'Aufgaben',
  mitglieder: 'Mitglieder',
  regionen: 'Regionen',
  abkuerzungen: 'Abkuerzungen',
};

// Build comprehensive AI system prompt
function buildSystemPrompt(courseId: string, course: Course): string {
  const isEnglish = courseId === 'english-test3';

  const modules = isEnglish
    ? `Module in der App:
- Grammatik-Lektionen (Conditional III, Indirect Speech, etc.)
- Vokabel-Lernkarten (6 Sets: Unit 6A, 6B, Australia A, B, Unit 7A, 7B)
- Vokabel-Matching Spiel
- Vokabel-Quiz (MC aus Vokabeln)
- Quiz (${course.quizQuestions?.length || 0} MC-Fragen)
- Lueckentext (${course.fillInQuestions?.length || 0} Uebungen)
- Indirect Speech Transform (${course.transformQuestions?.length || 0} Saetze)`
    : `Module in der App:
- Audio-Lektionen (8 Lektionen)
- Lernkarten
- Quiz (MC-Fragen)
- Mindmaps
- Schriftliches Quiz`;

  return `Du bist ein freundlicher Lern-Tutor fuer Adriana (14 Jahre, 4. Klasse Mittelschule Wien).

Du bekommst den GESAMTEN Lernfortschritt fuer den Kurs "${course.title}":
- Quiz-Ergebnisse (MC-Fragen)
- Lueckentext-Ergebnisse (Fill-in-the-blank)
- Indirect Speech Uebungen (Satzumwandlung)
- Vokabel-Lernkarten (gewusst/nicht gewusst pro Kategorie)
- Vokabel-Matching (Effizienz)
- Grammatik-Lektionen (gelesen/nicht gelesen)

${modules}

Analysiere ALLES zusammen und gib eine Gesamteinschaetzung.

Antworte auf DEUTSCH in diesem Format:
BEGRUESSUNG: [Persoenliche Begruessung mit Bezug auf letzte Aktivitaet, 1-2 Saetze]
STARK: [Themen die gut laufen - aus ALLEN Modulen, kommagetrennt]
UEBEN: [Themen die noch Uebung brauchen, kommagetrennt]
TIPP: [Konkreter Lerntipp fuer das schwaechste Thema, 1-2 Saetze]
EMPFEHLUNG: [3-5 konkrete Schritte die auf Module IN DER APP verweisen, nummeriert]
MOTIVATION: [Ermutigung, 1 Satz]

WICHTIG:
- Empfehlungen MUESSEN auf konkrete Module in der App verweisen
- Beispiele: 'Mach den Lueckentext fuer Conditional III', 'Uebe Vokabel-Karten Set Unit 6A', 'Lies die Grammatik-Lektion zu Indirect Speech'
- Beruecksichtige ALLE Datenquellen, nicht nur Quizze
- Wenn Lektionen nicht gelesen wurden, empfiehl das ZUERST
- Wenn Flashcards viele knew=false haben, empfiehl Wiederholung
- Nenne konkrete Zahlen (z.B. '12 von 23 Vokabeln noch offen')
- Nenne NIEMALS Frage-IDs oder technische Bezeichnungen
- Sprache: einfaches Deutsch, du-Form, ermutigend aber ehrlich`;
}

export function Lernstand() {
  const navigate = useNavigate();
  const { courseId: urlCourseId } = useParams<{ courseId: string }>();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<LernstandFeedback | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progressData, setProgressData] = useState<FullProgressData | null>(null);
  const [hasAnyData, setHasAnyData] = useState(false);
  const [course, setCourse] = useState<Course | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);

  // Default to first course if no courseId in URL
  const courseId = urlCourseId || getCourseList()[0]?.id || 'eu-geographie';

  // Load course data
  useEffect(() => {
    async function loadCourse() {
      const loadedCourse = await getCourse(courseId);
      if (loadedCourse) {
        setCourse(loadedCourse);
        setQuizQuestions(loadedCourse.quizQuestions || []);
      }
    }
    loadCourse();
  }, [courseId]);

  const loadAllProgress = async (forceRefresh = false) => {
    if (!user?.id || !course) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Check cache (unless force refresh)
    const cacheKey = `lernstand-full-${user.id}-${courseId}`;
    if (!forceRefresh) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const CACHE_TTL = 5 * 60 * 1000;
          if (Date.now() - (parsed.timestamp || 0) < CACHE_TTL) {
            setFeedback(parsed.feedback);
            setProgressData(parsed.progressData);
            setHasAnyData(parsed.hasAnyData);
            setIsLoading(false);
            return;
          }
        } catch {
          sessionStorage.removeItem(cacheKey);
        }
      }
    }

    try {
      // Load ALL data in parallel
      const [
        quizResults,
        fillInResults,
        transformResults,
        flashcardProgress,
        vocabMatchLogs,
        lessonProgress,
        lastActivity,
      ] = await Promise.all([
        // Quiz results
        getQuizResults(user.id, courseId, 50),

        // Fill-in from quiz_results (mode='fill-in') or activity_log
        supabase
          .from('activity_log')
          .select('details, created_at')
          .eq('user_id', user.id)
          .eq('action', 'fill_in')
          .order('created_at', { ascending: false })
          .limit(20),

        // Transform from activity_log
        supabase
          .from('activity_log')
          .select('details, created_at')
          .eq('user_id', user.id)
          .eq('action', 'transform_quiz')
          .order('created_at', { ascending: false })
          .limit(20),

        // Flashcard progress
        supabase
          .from('flashcard_progress')
          .select('flashcard_id, knew')
          .eq('user_id', user.id)
          .eq('course_id', courseId),

        // Vocab match from activity_log
        supabase
          .from('activity_log')
          .select('details, created_at')
          .eq('user_id', user.id)
          .eq('action', 'vocab_match')
          .order('created_at', { ascending: false })
          .limit(20),

        // Lesson progress
        supabase
          .from('lesson_progress')
          .select('lesson_id, completed')
          .eq('user_id', user.id)
          .eq('course_id', courseId),

        // Last activity
        supabase
          .from('activity_log')
          .select('action, created_at, details')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1),
      ]);

      // Process Quiz data
      const quizData = quizResults || [];
      const quizWrongByCategory: Record<string, number> = {};
      quizData.forEach(r => {
        if (r.wrong_questions && Array.isArray(r.wrong_questions)) {
          r.wrong_questions.forEach((qId: string) => {
            const q = quizQuestions.find(qq => qq.id === qId);
            if (q) {
              quizWrongByCategory[q.category] = (quizWrongByCategory[q.category] || 0) + 1;
            }
          });
        }
      });

      // Process Fill-in data
      const fillInData = fillInResults.data || [];
      const fillInScores = fillInData
        .map(r => (r.details as { percentage?: number })?.percentage)
        .filter((p): p is number => p !== undefined);

      // Process Transform data
      const transformData = transformResults.data || [];
      const transformScores = transformData
        .map(r => (r.details as { percentage?: number })?.percentage)
        .filter((p): p is number => p !== undefined);

      // Process Flashcard data by category
      const fcData = flashcardProgress.data || [];
      const flashcardsByCategory: Record<string, { known: number; unknown: number; total: number }> = {};
      let totalKnown = 0;
      let totalUnknown = 0;

      // Get category from flashcard ID
      course.flashcards?.forEach(fc => {
        if (!flashcardsByCategory[fc.category]) {
          flashcardsByCategory[fc.category] = { known: 0, unknown: 0, total: 0 };
        }
        flashcardsByCategory[fc.category].total++;

        const progress = fcData.find(p => p.flashcard_id === fc.id);
        if (progress) {
          if (progress.knew) {
            flashcardsByCategory[fc.category].known++;
            totalKnown++;
          } else {
            flashcardsByCategory[fc.category].unknown++;
            totalUnknown++;
          }
        }
      });

      // Process Vocab Match data
      const vmData = vocabMatchLogs.data || [];
      const vmEfficiencies = vmData.map(r => {
        const d = r.details as { pairs_matched?: number; attempts?: number };
        if (d.pairs_matched && d.attempts && d.attempts > 0) {
          return Math.round((d.pairs_matched / d.attempts) * 100);
        }
        return null;
      }).filter((e): e is number => e !== null);

      // Process Lesson progress
      const lessonData = lessonProgress.data || [];
      const readLessonIds = new Set(lessonData.filter(l => l.completed).map(l => l.lesson_id));
      // Cast to common interface since we only need id and title
      const textLessons = (course.textLessons || []) as Array<{ id: string; title: string }>;
      const audioLessons = (course.lessons || []) as Array<{ id: string; title: string }>;
      const allLessons = [...textLessons, ...audioLessons];
      const lessonsRead = allLessons.filter(l => readLessonIds.has(l.id)).map(l => l.title);
      const lessonsNotRead = allLessons.filter(l => !readLessonIds.has(l.id)).map(l => l.title);

      // Last activity
      const lastAct = lastActivity.data?.[0];
      const lastActivityType = lastAct?.action || '';
      const lastActivityTime = lastAct?.created_at || '';

      // Build progress data object
      const data: FullProgressData = {
        quizCount: quizData.length,
        quizAvg: quizData.length > 0
          ? Math.round(quizData.reduce((sum, r) => sum + r.percentage, 0) / quizData.length)
          : 0,
        quizLast: quizData[0]?.percentage ?? null,
        quizWrongByCategory,

        fillInCount: fillInData.length,
        fillInAvg: fillInScores.length > 0
          ? Math.round(fillInScores.reduce((a, b) => a + b, 0) / fillInScores.length)
          : 0,
        fillInLast: fillInScores[0] ?? null,

        transformCount: transformData.length,
        transformAvg: transformScores.length > 0
          ? Math.round(transformScores.reduce((a, b) => a + b, 0) / transformScores.length)
          : 0,
        transformLast: transformScores[0] ?? null,

        flashcardsByCategory,
        flashcardsTotalKnown: totalKnown,
        flashcardsTotalUnknown: totalUnknown,

        vocabMatchCount: vmData.length,
        vocabMatchAvgEfficiency: vmEfficiencies.length > 0
          ? Math.round(vmEfficiencies.reduce((a, b) => a + b, 0) / vmEfficiencies.length)
          : 0,

        lessonsRead,
        lessonsNotRead,

        lastActivityType,
        lastActivityTime,
      };

      setProgressData(data);

      // Check if we have any data
      const hasData = data.quizCount > 0 ||
        data.fillInCount > 0 ||
        data.transformCount > 0 ||
        data.flashcardsTotalKnown > 0 ||
        data.flashcardsTotalUnknown > 0 ||
        data.vocabMatchCount > 0 ||
        data.lessonsRead.length > 0;

      setHasAnyData(hasData);

      if (!hasData) {
        setIsLoading(false);
        return;
      }

      // Generate AI feedback
      const feedbackText = await generateFeedback(data);
      const parsed = parseFeedback(feedbackText);
      setFeedback(parsed);

      // Save to Supabase
      await saveAiFeedback(user.id, 'lernstand', feedbackText, parsed as Record<string, string>, {
        courseId,
        scoreInfo: `Quiz ${data.quizAvg}%, Fill-in ${data.fillInAvg}%, FC ${data.flashcardsTotalKnown}/${data.flashcardsTotalKnown + data.flashcardsTotalUnknown}`,
      });

      // Cache
      sessionStorage.setItem(cacheKey, JSON.stringify({
        feedback: parsed,
        progressData: data,
        hasAnyData: hasData,
        timestamp: Date.now(),
      }));

    } catch (error) {
      console.error('Error loading progress:', error);
    }

    setIsLoading(false);
  };

  const generateFeedback = async (data: FullProgressData): Promise<string> => {
    if (!course) {
      throw new Error('Kurs nicht gefunden');
    }

    // Build comprehensive data message
    const dataMessage = `
=== VOLLSTAENDIGER LERNFORTSCHRITT FUER ${course.title} ===

1. QUIZ (MC-Fragen):
- Anzahl Quizze gemacht: ${data.quizCount}
- Durchschnitt: ${data.quizAvg}%
- Letztes Ergebnis: ${data.quizLast !== null ? `${data.quizLast}%` : 'noch nicht gemacht'}
- Fehler nach Kategorie: ${JSON.stringify(data.quizWrongByCategory)}

2. LUECKENTEXT:
- Anzahl Sessions: ${data.fillInCount}
- Durchschnitt: ${data.fillInAvg}%
- Letztes Ergebnis: ${data.fillInLast !== null ? `${data.fillInLast}%` : 'noch nicht gemacht'}

3. INDIRECT SPEECH (Transform):
- Anzahl Sessions: ${data.transformCount}
- Durchschnitt: ${data.transformAvg}%
- Letztes Ergebnis: ${data.transformLast !== null ? `${data.transformLast}%` : 'noch nicht gemacht'}

4. VOKABEL-LERNKARTEN (pro Kategorie):
${Object.entries(data.flashcardsByCategory)
  .map(([cat, stats]) => `- ${categoryLabels[cat] || cat}: ${stats.known} gewusst, ${stats.unknown} nicht gewusst, ${stats.total - stats.known - stats.unknown} noch nie geuebt`)
  .join('\n')}
- GESAMT: ${data.flashcardsTotalKnown} gewusst, ${data.flashcardsTotalUnknown} nicht gewusst

5. VOKABEL-MATCHING:
- Anzahl Spiele: ${data.vocabMatchCount}
- Durchschnittliche Effizienz: ${data.vocabMatchAvgEfficiency}%

6. GRAMMATIK-LEKTIONEN:
- Gelesen: ${data.lessonsRead.length > 0 ? data.lessonsRead.join(', ') : 'keine'}
- Nicht gelesen: ${data.lessonsNotRead.length > 0 ? data.lessonsNotRead.join(', ') : 'alle gelesen'}

7. LETZTE AKTIVITAET:
- Typ: ${data.lastActivityType || 'keine'}
- Zeit: ${data.lastActivityTime ? new Date(data.lastActivityTime).toLocaleString('de-DE') : 'unbekannt'}

Erstelle den Feedback-Bogen basierend auf ALLEN diesen Daten.`;

    const responseData = await callClaude({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1500,
      system: buildSystemPrompt(courseId, course),
      messages: [{ role: 'user', content: dataMessage }],
    }, 'lernstand-feedback');

    return responseData.content?.[0]?.text || '';
  };

  const parseFeedback = (text: string): LernstandFeedback => {
    const sections: LernstandFeedback = {};
    const labels: (keyof LernstandFeedback)[] = ['BEGRUESSUNG', 'STARK', 'UEBEN', 'TIPP', 'EMPFEHLUNG', 'MOTIVATION'];

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      const nextLabels = labels.slice(i + 1);
      const lookahead = nextLabels.length > 0
        ? `(?=[-\u2022*]*\\s*\\**(?:${nextLabels.join('|')})\\**:)`
        : '';
      const regex = new RegExp(
        `[-\u2022*]*\\s*\\**${label}\\**:?\\s*(.+?)${lookahead || '(?=$)'}`,
        'si'
      );
      const match = text.match(regex);
      if (match && match[1]) {
        sections[label] = match[1].trim();
      }
    }
    return sections;
  };

  useEffect(() => {
    if (course && quizQuestions.length >= 0) {
      loadAllProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, courseId, course, quizQuestions.length]);

  const refreshFeedback = () => {
    sessionStorage.removeItem(`lernstand-full-${user?.id}-${courseId}`);
    loadAllProgress(true);
  };

  // No data yet
  if (!isLoading && !hasAnyData) {
    return (
      <div style={{ padding: '20px', paddingBottom: '100px' }}>
        <Link to={urlCourseId ? `/course/${urlCourseId}` : '/'} className="back-link">
          <ArrowLeft size={18} />
          Zurueck
        </Link>

        <h1 style={{ color: '#ffd54f', fontSize: '24px', marginBottom: '20px', marginTop: '20px' }}>
          Dein Lernstand {course?.title && `- ${course.title}`}
        </h1>

        <div style={{
          background: 'rgba(66, 165, 245, 0.1)',
          border: '1px solid rgba(66, 165, 245, 0.3)',
          borderRadius: '16px',
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
          <div style={{ color: '#e8eaf6', fontSize: '18px', marginBottom: '12px' }}>
            Noch keine Lernaktivitaeten
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
            Mach ein Quiz, uebe Vokabeln oder lies eine Lektion, dann bekommst du hier deinen KI-Lernplan!
          </div>
          <button
            onClick={() => navigate(`/course/${courseId}`)}
            style={{
              background: '#ffd54f',
              border: 'none',
              borderRadius: '12px',
              color: '#1a1a2e',
              padding: '14px 28px',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Zum Kurs
          </button>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return <LoadingSpinner text="KI analysiert deinen gesamten Lernstand..." />;
  }

  // Calculate combined error data for chart
  const combinedErrors: Record<string, { quiz: number; fillIn: number; flashcard: number }> = {};

  // Add quiz errors
  if (progressData) {
    Object.entries(progressData.quizWrongByCategory).forEach(([cat, count]) => {
      if (!combinedErrors[cat]) combinedErrors[cat] = { quiz: 0, fillIn: 0, flashcard: 0 };
      combinedErrors[cat].quiz = count;
    });

    // Add flashcard weaknesses
    Object.entries(progressData.flashcardsByCategory).forEach(([cat, stats]) => {
      if (!combinedErrors[cat]) combinedErrors[cat] = { quiz: 0, fillIn: 0, flashcard: 0 };
      combinedErrors[cat].flashcard = stats.unknown;
    });
  }

  const maxTotal = Math.max(
    ...Object.values(combinedErrors).map(e => e.quiz + e.fillIn + e.flashcard),
    1
  );

  return (
    <div style={{ padding: '20px', paddingBottom: '100px' }}>
      <Link to={urlCourseId ? `/course/${urlCourseId}` : '/'} className="back-link">
        <ArrowLeft size={18} />
        Zurueck
      </Link>

      <h1 style={{ color: '#ffd54f', fontSize: '24px', marginBottom: '20px', marginTop: '20px' }}>
        Dein Lernstand {course?.title && `- ${course.title}`}
      </h1>

      {/* Begruessung */}
      {feedback?.BEGRUESSUNG && (
        <div style={{
          background: 'rgba(255, 213, 79, 0.1)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '16px',
          fontSize: '17px',
          color: '#e8eaf6',
          lineHeight: '1.6',
        }}>
          {feedback.BEGRUESSUNG}
        </div>
      )}

      {/* Statistik-Karten - now showing all modules */}
      {progressData && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
          {progressData.quizCount > 0 && (
            <div style={{
              background: 'rgba(33, 150, 243, 0.15)',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#42a5f5', fontSize: '22px', fontWeight: 700 }}>{progressData.quizAvg}%</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Quiz ({progressData.quizCount}x)</div>
            </div>
          )}

          {progressData.fillInCount > 0 && (
            <div style={{
              background: 'rgba(0, 188, 212, 0.15)',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#00bcd4', fontSize: '22px', fontWeight: 700 }}>{progressData.fillInAvg}%</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Lueckentext</div>
            </div>
          )}

          {progressData.transformCount > 0 && (
            <div style={{
              background: 'rgba(233, 30, 99, 0.15)',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#e91e63', fontSize: '22px', fontWeight: 700 }}>{progressData.transformAvg}%</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Transform</div>
            </div>
          )}

          {(progressData.flashcardsTotalKnown > 0 || progressData.flashcardsTotalUnknown > 0) && (
            <div style={{
              background: 'rgba(255, 152, 0, 0.15)',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#ff9800', fontSize: '22px', fontWeight: 700 }}>
                {progressData.flashcardsTotalKnown}/{progressData.flashcardsTotalKnown + progressData.flashcardsTotalUnknown}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Vokabeln</div>
            </div>
          )}

          {progressData.lessonsRead.length > 0 && (
            <div style={{
              background: 'rgba(156, 39, 176, 0.15)',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#ab47bc', fontSize: '22px', fontWeight: 700 }}>
                {progressData.lessonsRead.length}/{progressData.lessonsRead.length + progressData.lessonsNotRead.length}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Lektionen</div>
            </div>
          )}
        </div>
      )}

      {/* STARK */}
      {feedback?.STARK && (
        <div style={{
          background: 'rgba(76, 175, 80, 0.12)',
          borderLeft: '4px solid #4caf50',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          <div style={{ color: '#4caf50', fontWeight: 700, marginBottom: '8px' }}>
            ✅ Das laeuft gut
          </div>
          <div style={{ color: '#e8eaf6', lineHeight: '1.5' }}>{feedback.STARK}</div>
        </div>
      )}

      {/* UEBEN */}
      {feedback?.UEBEN && (
        <div style={{
          background: 'rgba(255, 152, 0, 0.12)',
          borderLeft: '4px solid #ff9800',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          <div style={{ color: '#ff9800', fontWeight: 700, marginBottom: '8px' }}>
            ⚠️ Hier noch ueben
          </div>
          <div style={{ color: '#e8eaf6', lineHeight: '1.5' }}>{feedback.UEBEN}</div>
        </div>
      )}

      {/* TIPP */}
      {feedback?.TIPP && (
        <div style={{
          background: 'rgba(66, 165, 245, 0.12)',
          borderLeft: '4px solid #42a5f5',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          <div style={{ color: '#42a5f5', fontWeight: 700, marginBottom: '8px' }}>
            💡 Lerntipp
          </div>
          <div style={{ color: '#e8eaf6', lineHeight: '1.5' }}>{feedback.TIPP}</div>
        </div>
      )}

      {/* EMPFEHLUNG */}
      {feedback?.EMPFEHLUNG && (
        <div style={{
          background: 'rgba(255, 213, 79, 0.12)',
          borderLeft: '4px solid #ffd54f',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          <div style={{ color: '#ffd54f', fontWeight: 700, marginBottom: '8px' }}>
            🎯 Naechste Schritte
          </div>
          <div style={{ color: '#e8eaf6', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{feedback.EMPFEHLUNG}</div>
        </div>
      )}

      {/* MOTIVATION */}
      {feedback?.MOTIVATION && (
        <div style={{
          background: 'rgba(171, 71, 188, 0.12)',
          borderLeft: '4px solid #ab47bc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          <div style={{ color: '#ab47bc', fontWeight: 700, marginBottom: '8px' }}>
            💪 Motivation
          </div>
          <div style={{ color: '#e8eaf6', lineHeight: '1.5' }}>{feedback.MOTIVATION}</div>
        </div>
      )}

      {/* Combined Error Chart */}
      {Object.keys(combinedErrors).length > 0 && (
        <>
          <h2 style={{ color: '#ffd54f', fontSize: '18px', marginTop: '24px', marginBottom: '16px' }}>
            Schwaechen nach Kategorie
          </h2>
          <div style={{ marginBottom: '8px', display: 'flex', gap: '16px', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
            <span>🔵 Quiz-Fehler</span>
            <span>🟠 Vokabel-Schwaechen</span>
          </div>
          {Object.entries(combinedErrors)
            .sort(([, a], [, b]) => (b.quiz + b.flashcard) - (a.quiz + a.flashcard))
            .slice(0, 8)
            .map(([category, errors]) => {
              const total = errors.quiz + errors.flashcard;
              if (total === 0) return null;

              const quizPct = Math.round((errors.quiz / maxTotal) * 100);
              const fcPct = Math.round((errors.flashcard / maxTotal) * 100);

              return (
                <div key={category} style={{ marginBottom: '12px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '13px',
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: '4px',
                  }}>
                    <span>{categoryLabels[category] || category}</span>
                    <span>
                      {errors.quiz > 0 && <span style={{ color: '#42a5f5' }}>{errors.quiz} Quiz</span>}
                      {errors.quiz > 0 && errors.flashcard > 0 && ' · '}
                      {errors.flashcard > 0 && <span style={{ color: '#ff9800' }}>{errors.flashcard} Vok.</span>}
                    </span>
                  </div>
                  <div style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    height: '10px',
                    overflow: 'hidden',
                    display: 'flex',
                  }}>
                    {errors.quiz > 0 && (
                      <div style={{
                        background: '#42a5f5',
                        height: '100%',
                        width: `${quizPct}%`,
                      }} />
                    )}
                    {errors.flashcard > 0 && (
                      <div style={{
                        background: '#ff9800',
                        height: '100%',
                        width: `${fcPct}%`,
                      }} />
                    )}
                  </div>
                </div>
              );
            })
          }
        </>
      )}

      {/* Buttons */}
      <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button
          onClick={refreshFeedback}
          disabled={isLoading}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '12px',
            color: '#e8eaf6',
            padding: '14px',
            fontSize: '15px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          🔄 Neu analysieren
        </button>

        <button
          onClick={() => navigate(`/feedback/${courseId}`)}
          style={{
            background: 'rgba(171, 71, 188, 0.15)',
            border: '1px solid rgba(171, 71, 188, 0.3)',
            borderRadius: '12px',
            color: '#ce93d8',
            padding: '14px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          📜 Feedback-Historie
        </button>

        <button
          onClick={() => navigate(`/course/${courseId}`)}
          style={{
            background: '#ffd54f',
            border: 'none',
            borderRadius: '12px',
            color: '#1a1a2e',
            padding: '14px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          📚 Zum Kurs
        </button>
      </div>
    </div>
  );
}
